/**
 * Utility functions for income validation requirements
 */

/**
 * Calculate the required income years (current year + 2 previous years)
 * For example, if it's January 2025, the required years are: 2025, 2024, 2023
 */
export const getRequiredIncomeYears = (): number[] => {
  const currentYear = new Date().getFullYear();
  return [currentYear, currentYear - 1, currentYear - 2];
};

/**
 * Check which required income years are missing from the provided incomes
 */
export type IncomeYearsValidationDocument =
  | {
      finalizedAt?: string | Date | null | undefined;
      reviewedAt?: string | Date | null | undefined;
    }
  | undefined
  | null;

export const getMissingIncomeYears = (
  incomes: { year: number }[],
  document: IncomeYearsValidationDocument,
): number[] => {
  const isDocumentFinalizedOrReviewed =
    document !== undefined &&
    document !== null &&
    (!!document.finalizedAt || !!document.reviewedAt);

  if (isDocumentFinalizedOrReviewed) {
    return [];
  }

  const requiredYears = getRequiredIncomeYears();
  const existingYears = new Set(incomes.map((income) => income.year));

  return requiredYears.filter((year) => !existingYears.has(year));
};

/**
 * Check if all required income years are present
 */
export const hasAllRequiredIncomeYears = (
  incomes: { year: number }[],
  document: IncomeYearsValidationDocument,
): boolean => {
  return getMissingIncomeYears(incomes, document).length === 0;
};

/**
 * Generate a user-friendly message about missing income years
 */
export const getMissingIncomeYearsMessage = (
  missingYears: number[],
): string => {
  if (missingYears.length === 0) return "";

  if (missingYears.length === 1) {
    return `Please add income for ${missingYears[0]}`;
  }

  if (missingYears.length === 2) {
    return `Please add income for ${missingYears[0]} and ${missingYears[1]}`;
  }

  return `Please add income for ${missingYears.slice(0, -1).join(", ")}, and ${missingYears[missingYears.length - 1]}`;
};
